import React, { useState, useEffect } from 'react';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { usePostPromptMutation } from '../store/slices/truPromptSlice';
import Swal from 'sweetalert2';
import '@sweetalert2/theme-dark';

const SaveModal = ({ show, selectedPrompt, setShowSaveDialog, setOpenAIResults }) => {
    const [saveCategory, setSaveCategory] = useState("SEO");
    const [postPrompt] = usePostPromptMutation();

    useEffect(() => {
        // If the prompt is already saved, close the modal and show message
        if (selectedPrompt?.savedStatus) {
            setShowSaveDialog(false);
            Swal.fire({
                title: 'Info',
                text: 'This prompt is already saved in the database',
                icon: 'info',
                confirmButtonText: 'OK',
                theme: 'dark'
            });
        }
    }, [selectedPrompt, setShowSaveDialog]);

    const handleClose = () => {
        setShowSaveDialog(false);
    };

    const handleRadioChange = (e) => {
        setSaveCategory(e.target.value);
    };

    const savePrompt = async () => {
        try {
            // Create the prompt object with all required fields
            const promptToSave = {
            prompt: selectedPrompt.prompt,
            optimizer: selectedPrompt.optimizer,
                userPrompt: selectedPrompt.userPrompt,
                category: saveCategory,
                language: selectedPrompt.language || 'EN',
                teams: '',
                mostUsedPromptCount: 0,
                tags: '',
                includeKeywords: '',
                excludeKeywords: '',
                mustHaveKeywords: '',
                description: ''
            };

            // Save to MongoDB
            const response = await postPrompt(promptToSave).unwrap();

            if (response && response._id) {
                // Update local state
                if (setOpenAIResults) {
                    setOpenAIResults(prevResults => {
                        return prevResults.map(result => {
                            if (result.id === selectedPrompt.id) {
                                return { 
                                    ...result, 
                                    savedStatus: true,
                                    promptDBId: response._id 
                                };
                            }
                            return result;
                        });
                    });
                }

                // Show success message
                Swal.fire({
                    title: 'Success!',
                    text: 'Prompt saved successfully!',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    theme: 'dark'
                }).then(() => {
                    handleClose();
                });
            }
        } catch (error) {
            console.error('Save error:', error);
            Swal.fire({
                    title: 'Error!',
                text: error.data?.message || 'Failed to save prompt',
                    icon: 'error',
                    confirmButtonText: 'OK',
                    theme: 'dark'
                });
        }
    };

    // Don't render the modal if the prompt is already saved
    if (selectedPrompt?.savedStatus) {
        return null;
    }

    const SaveButton = () => {
        return (
            <Button variant="primary" onClick={savePrompt} className="SaveBtn">
                Save
            </Button>
        );
    };

    return (
        <Modal show={show} onHide={handleClose} className="customModal" centered>
            <Modal.Header closeButton>
                <Modal.Title>Save this prompt</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                <p>Do you want to save this prompt to the: {saveCategory}?</p>
                    <div className="form-check">
                        <input
                            type="radio"
                            className="form-check-input"
                            name="category"
                            value="SEO"
                            checked={saveCategory === "SEO"}
                            onChange={handleRadioChange}
                            id="seoCategory"
                        />
                        <label className="form-check-label" htmlFor="seoCategory">
                            SEO
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            type="radio"
                            className="form-check-input"
                            name="category"
                            value="Design"
                            checked={saveCategory === "Design"}
                            onChange={handleRadioChange}
                            id="designCategory"
                        />
                        <label className="form-check-label" htmlFor="designCategory">
                            Design
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            type="radio"
                            className="form-check-input"
                            name="category"
                            value="Communication"
                            checked={saveCategory === "Communication"}
                            onChange={handleRadioChange}
                            id="communicationCategory"
                        />
                        <label className="form-check-label" htmlFor="communicationCategory">
                            Communication
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            type="radio"
                            className="form-check-input"
                            name="category"
                            value="Web Development"
                            checked={saveCategory === "Web Development"}
                            onChange={handleRadioChange}
                            id="webDevCategory"
                        />
                        <label className="form-check-label" htmlFor="webDevCategory">
                            Web Development
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            type="radio"
                            className="form-check-input"
                            name="category"
                            value="Lead Generation"
                            checked={saveCategory === "Lead Generation"}
                            onChange={handleRadioChange}
                            id="leadGenCategory"
                        />
                        <label className="form-check-label" htmlFor="leadGenCategory">
                            Lead Generation
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            type="radio"
                            className="form-check-input"
                            name="category"
                            value="Lifecycle Marketing"
                            checked={saveCategory === "Lifecycle Marketing"}
                            onChange={handleRadioChange}
                            id="lifecycleCategory"
                        />
                        <label className="form-check-label" htmlFor="lifecycleCategory">
                            Lifecycle Marketing
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            type="radio"
                            className="form-check-input"
                            name="category"
                            value="Business Development"
                            checked={saveCategory === "Business Development"}
                            onChange={handleRadioChange}
                            id="businessCategory"
                        />
                        <label className="form-check-label" htmlFor="businessCategory">
                            Business Development
                        </label>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <SaveButton />
            </Modal.Footer>
        </Modal>
    );
};

export default SaveModal;